import type { StateCreator } from 'zustand'
import { getMyFlags } from '@/service/featureFlagsApi'

/** A forced reload within this long of the last one is skipped. */
export const FEATURE_FLAGS_REFRESH_MIN_MS = 5_000

export type ServerFlags = Readonly<Record<string, boolean | string>>

/**
 * The person's feature flags, from `GET /v2/me/flags`: every `frontend`-tier flag, evaluated by
 * the web tier's one evaluator. Same shape as `featureAccessSlice`, in its own store
 * (`featureFlagsStore.ts`) so the ~25 components that read a flag do not import the whole app
 * store. Until they load (or if the request fails) readers use the generated registry defaults,
 * which is also what the evaluator answers where PostHog has no opinion. A failed reload keeps
 * the last good answer.
 */
export interface FeatureFlagsState {
  /** Flags for `serverFlagsUserId`; null = not loaded for the current user. */
  serverFlags: ServerFlags | null
  serverFlagsUserId: string | null
  serverFlagsLoading: boolean
  /** The user the in-flight (or last) request is for; a newer request supersedes it. */
  serverFlagsRequestedFor: string | null
  serverFlagsLoadedAt: number
  /** `force` reloads even when this user's flags are already loaded. */
  loadServerFlags: (userId: string, force?: boolean) => Promise<void>
  /** Forget the loaded flags (they depend on the active organisation). */
  clearServerFlags: () => void
}

export const featureFlagsSlice: StateCreator<FeatureFlagsState> = (set, get) => {
  // At most one delayed retry for a forced reload that arrived too soon.
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  // Bumped by each request and by clearServerFlags: only the latest answer lands, so flags
  // requested under the previous organisation never overwrite a newer answer.
  let generation = 0
  return {
    serverFlags: null,
    serverFlagsUserId: null,
    serverFlagsLoading: false,
    serverFlagsRequestedFor: null,
    serverFlagsLoadedAt: 0,

    loadServerFlags: async (userId: string, force = false) => {
      const state = get()
      if (state.serverFlagsLoading && state.serverFlagsRequestedFor === userId) return
      if (!state.serverFlagsLoading && state.serverFlagsUserId === userId) {
        if (!force) return
        const wait = FEATURE_FLAGS_REFRESH_MIN_MS - (Date.now() - state.serverFlagsLoadedAt)
        if (wait > 0) {
          // Too soon to reload, but the flags may be stale: try again once the wait is over.
          if (!retryTimer) {
            retryTimer = setTimeout(() => {
              retryTimer = null
              void get().loadServerFlags(userId, true)
            }, wait)
          }
          return
        }
      }
      // Another user's flags are never shown to this one while theirs load.
      if (state.serverFlagsUserId !== userId) {
        set({ serverFlags: null, serverFlagsUserId: null, serverFlagsLoadedAt: 0 })
      }
      set({ serverFlagsLoading: true, serverFlagsRequestedFor: userId })
      const thisRequest = ++generation
      let flags: ServerFlags | null = null
      try {
        flags = (await getMyFlags()).flags
      } catch {
        // Keep what is loaded (or the registry defaults): an outage never changes a flag.
      }
      // A request for another user (session switch) started meanwhile: its answer wins.
      if (get().serverFlagsRequestedFor !== userId || thisRequest !== generation) return
      if (flags === null) {
        // Not marked loaded for this user, so the next load retries.
        set({ serverFlagsLoading: false })
        return
      }
      set({
        serverFlags: flags,
        serverFlagsUserId: userId,
        serverFlagsLoading: false,
        serverFlagsLoadedAt: Date.now(),
      })
    },

    clearServerFlags: () => {
      generation++
      set({
        serverFlags: null,
        serverFlagsUserId: null,
        serverFlagsLoading: false,
        serverFlagsRequestedFor: null,
        serverFlagsLoadedAt: 0,
      })
    },
  }
}
